"use client";
import React, { useRef, useEffect, useCallback, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardHeader,
  CardFooter,
  CardDescription,
  CardContent,
} from "../../../../components/ui/card";

const BlogPost = () => {
  const router = useRouter();
  const cardRefs = useRef([]);
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const blogPosts = [
    {
      id: 1,
      title: "How Traveling with a Mentor Can Change Your Life",
      date: "15 Jan 2025",
      description:
        "Explore how traveling with a mentor creates room for real conversations, personal breakthroughs, and lifelong guidance—all while discovering new places.",
      image:
        "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=400&h=400&fit=crop",
      alt: "Mentor guiding a traveler in nature",
      category: "Growth & Mentorship",
      readTime: "2 min read",
    },
    {
      id: 2,
      title: "Why Travel Is the Best Way to Find Clarity in Your 20s",
      date: "24 Jan 2025",
      description:
        "Your 20s can feel chaotic. Learn why getting out of your comfort zone and into the world might be the most powerful way to find direction and self-awareness.",
      image:
        "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=400&h=400&fit=crop",
      alt: "Young solo traveler looking at mountains",
      category: "Self-Discovery",
      readTime: "3 min read",
    },
    {
      id: 3,
      title: "Why Community-Based Travel Is the Future of Exploration",
      date: "01 March 2025",
      description:
        "The most unforgettable trips aren't about places—they're about people. Discover how curated group travel is transforming how we connect, explore, and grow.",
      image:
        "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&h=400&fit=crop",
      alt: "Group of travelers sharing a moment",
      category: "Community Travel",
      readTime: "2 min read",
    },
    {
      id: 4,
      title:
        "How Founders Can Use Travel to Prevent Burnout (Without Falling Behind)",
      date: "11 March 2025",
      description:
        "Burnout is real—but the solution doesn’t have to be isolation. Learn how outcome-driven travel can recharge you while still fueling your business growth.",
      image:
        "https://images.unsplash.com/photo-1556740749-887f6717d7e4?w=400&h=400&fit=crop",
      alt: "Founder journaling at retreat",
      category: "Founder Wellness",
      readTime: "3 min read",
    },
    {
      id: 5,
      title: "Why Learning Feels Different When You’re Traveling",
      date: "20 May 2025",
      description:
        "Traditional classrooms can't match the spark of learning on the road. Here's why immersive environments, new contexts, and real-world exploration accelerate growth.",
      image:
        "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=400&h=400&fit=crop",
      alt: "Traveler learning outdoors",
      category: "Experiential Learning",
      readTime: "3 min read",
    },
  ];

  // Handle smooth scroll to specific card
const scrollToCard = (index) => {
  if (!scrollContainerRef.current || !cardRefs.current[index]) return;

  scrollContainerRef.current.scrollTo({
    left: cardRefs.current[index].offsetLeft,
    behavior: "smooth",
  });

  setCurrentIndex(index);
};


  // Handle mouse/touch drag scrolling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

 const handleScroll = useCallback(() => {
  if (!scrollContainerRef.current || isDragging) return;

  const container = scrollContainerRef.current;
  const center = container.scrollLeft + container.offsetWidth / 2;

  let closestIndex = 0;
  let closestDistance = Infinity;

  cardRefs.current.forEach((card, index) => {
    if (!card) return;
    const cardCenter = card.offsetLeft + card.offsetWidth / 2;
    const distance = Math.abs(center - cardCenter);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  setCurrentIndex(closestIndex);
}, [isDragging]);


  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  return (
    <section className="w-full bg-[#FFFFFFE6] py-6 sm:py-8 md:py-12">
      <motion.h2
        className="text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#FFAE00] tracking-[-0.5px] px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        Featured Blog Posts
      </motion.h2>

      {/* Scrollable Container */}
      <div className="relative mt-6 sm:mt-8 md:mt-12 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={scrollContainerRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing pb-4 snap-x snap-mandatory"
          style={{
            WebkitOverflowScrolling: "touch",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {blogPosts.map((post, index) => (
            <motion.div
              key={post.id}
              ref={(el) => (cardRefs.current[index] = el)}
              className="flex-shrink-0 w-[280px] sm:w-[300px] md:w-[320px] lg:w-[360px] snap-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                delay: index * 0.1,
              }}
            >
              <Card className="flex flex-col bg-white rounded-[11px] overflow-hidden border-[0.5px] border-[#0D0D0D]/20 transition-all duration-300 hover:shadow-[0_6px_25px_rgba(255,174,0,0.3)] h-full">
                <CardHeader className="p-4 sm:p-5 md:p-[22px] pb-0">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="font-bricolage font-bold text-[#0D0D0D] text-base sm:text-lg md:text-[19.8px] line-clamp-2">
                        {post.title}
                      </span>
                      <div className="flex gap-2">
                        <span className="font-jakarta font-normal text-[#0D0D0D]/60 text-xs sm:text-[13.2px]">
                          {post.date}
                        </span>
                        <span className="font-jakarta font-normal text-[#0D0D0D]/60 text-xs sm:text-[13.2px]">
                          • {post.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-4 sm:p-5 md:p-[22px] pt-4 sm:pt-5 md:pt-[22px] flex-grow">
                  <div className="relative w-full aspect-square overflow-hidden rounded-[11px] group">
                    <Image
                      src={post.image}
                      alt={post.alt}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                      sizes="(max-width: 640px) 280px, (max-width: 768px) 300px, (max-width: 1024px) 320px, 360px"
                      loading="lazy"
                    />
                    <div className="absolute w-full h-[66px] bottom-0 left-0 rounded-b-[11px] bg-gradient-to-t from-[rgba(0,0,0,0.3)] to-transparent">
                      <Button
                        onClick={() => router.push(`/blog/${post.id}`)}
                        className="absolute bottom-3 right-3 bg-[#FFAE00] hover:bg-[#E69A00] text-[#0D0D0D] rounded-[5.5px] px-3 sm:px-4 py-2 text-sm sm:text-[15.5px] font-bricolage transition-colors duration-200"
                      >
                        Read More
                      </Button>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="px-4 sm:px-5 md:px-[22px] pb-4 sm:pb-5 md:pb-[22px] pt-0">
                  <div className="flex flex-col gap-2">
                    <p className="font-jakarta text-sm sm:text-[15.4px] leading-5 sm:leading-6 line-clamp-3 text-[#0D0D0D]">
                      {post.description}
                    </p>
                    <span className="font-jakarta text-xs sm:text-sm text-[#0D0D0D]/60">
                      {post.category}
                    </span>
                  </div>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Navigation Controls Below Posts */}
        <div className="flex justify-center items-center gap-6 mt-6 sm:mt-8">
          {/* Desktop Navigation Arrows */}
          <div className="hidden sm:flex items-center gap-4">
            <button
              onClick={() => scrollToCard(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFAE00] hover:bg-[#E69A00] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:scale-100 disabled:opacity-50"
            >
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 fill-[#0D0D0D]"
                viewBox="0 0 24 24"
              >
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
              </svg>
            </button>

            {/* Dot Indicators */}
            <div className="flex gap-2">
              {blogPosts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToCard(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "bg-[#FFAE00] w-4 sm:w-6"
                      : "bg-[#0D0D0D]/30 hover:bg-[#0D0D0D]/50"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() =>
                scrollToCard(Math.min(blogPosts.length - 1, currentIndex + 1))
              }
              disabled={currentIndex === blogPosts.length - 1}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFAE00] hover:bg-[#E69A00] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:scale-100 disabled:opacity-50"
            >
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 fill-[#0D0D0D]"
                viewBox="0 0 24 24"
              >
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
              </svg>
            </button>
          </div>

          {/* Mobile-only Dot Indicators */}
          <div className="flex sm:hidden gap-2">
            {blogPosts.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToCard(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-[#FFAE00] w-4"
                    : "bg-[#0D0D0D]/30"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .snap-x {
          scroll-snap-type: x mandatory;
        }
        .snap-start {
          scroll-snap-align: start;
        }
      `}</style>
    </section>
  );
};

export default BlogPost;
